import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { authApi } from 'api';
import type { AppUser } from 'api/types';
import { identifyUser } from 'lib/analytics/posthog';

interface UserContextType {
  currentUser: AppUser | null;
  loading: boolean;
  refreshUser: () => Promise<void>;
}

const UserContext = createContext<UserContextType | null>(null);

export function UserProvider({ children }: { children: React.ReactNode }) {
  const { user: authUser, getIdToken, loading: authLoading } = useAuth();
  const [currentUser, setCurrentUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Function to load the current user
  const loadUser = useCallback(async () => {
    const token = await getIdToken();
    if (!token) {
      setCurrentUser(null);
      setLoading(false);
      return;
    }

    try {
      const user = await authApi.fetchCurrentUser();
      setCurrentUser(user);
    } catch (err) {
      console.error('Failed to load app user:', err);
      setCurrentUser(null);
    } finally {
      setLoading(false);
    }
  }, [getIdToken]);

  // Load user on auth loading change
  useEffect(() => {
    if (!authLoading) loadUser();
  }, [authLoading, authUser, loadUser]);

  useEffect(() => {
    if (!currentUser) return;
    identifyUser(currentUser);
  }, [currentUser]);

  return (
    <UserContext.Provider
      value={{
        currentUser,
        loading,
        refreshUser: loadUser,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

// Custom hook to use the UserContext
export function useUser() {
  const c = useContext(UserContext);
  if (!c) throw new Error('useUser must be used inside <UserProvider>');
  return c;
}
